import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { api, captcah_site_key } from "../../env";
import Loader from "../../compenents/loader/loader";
import { useForm, Controller } from "react-hook-form";
import { ControlledFileUploadWithView } from "./upload-file";
import { FaChevronRight, FaCheckCircle, FaPaperPlane } from "react-icons/fa";
import NotificationToast from "../../commun/components/NotificationToast";
import { useTranslation } from "react-i18next";
import { getTextDirection } from "../../utils/i18n-helper";
import "./update-derogation.css";

const UpdateDerogation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { setCode } = useAuth();
  const { i18n, t } = useTranslation();

  const [showLoader, setShowLoader] = useState(false);
  const [recaptchaIsValid, setRecaptchaIsValid] = useState(false);
  const [notification, setNotification] = useState(null);

  const defaultValues = {
    email: "",
    telephone: "",
    nom: "",
    prenom: "",
    nin: "",
    birthExtractNumber: "",
    carteNational: null,
    extraitNaissance: null,
    extraitNaissanceEpouse: null,
    contratLoyer: null,
    quittance: null,
    confirmation: false,
  };

  const fieldsConfig = [
    {
      name: "nom",
      label: { ar: "الاسم", fr: "Nom" },
      required: true,
    },
    {
      name: "prenom",
      label: { ar: "اللقب", fr: "Prénom" },
      required: true,
    },
    {
      name: "nin",
      label: { ar: "الرقم الوطني", fr: "NIN" },
      required: true,
    },
    {
      name: "birthExtractNumber",
      label: { ar: "رقم شهادة الميلاد", fr: "N° extrait de naissance" },
      required: true,
    },
    {
      name: "email",
      label: { ar: "البريد الإلكتروني", fr: "Email" },
      required: false,
    },
    {
      name: "telephone",
      label: { ar: "رقم الهاتف", fr: "Téléphone" },
      required: true,
    },
  ];

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    reset,
    setError,
    watch,
  } = useForm({
    defaultValues,
    mode: "onChange",
  });

  const confirmationValue = watch("confirmation");

  useEffect(() => {
    if (!id) return;

    const fetchDerogation = async () => {
      setShowLoader(true);
      try {
        const response = await api.get(`/api/derogations/${id}`);
        if (response.status >= 200 && response.status < 300) {
          const data = response.data.data;
          reset({
            email: data.email || "",
            telephone: data.telephone || "",
            nom: data.nom || "",
            prenom: data.prenom || "",
            nin: data.nin || "",
            birthExtractNumber: data.numero_certificat || "",
            carteNational: null,
            extraitNaissance: null,
            extraitNaissanceEpouse: null,
            contratLoyer: null,
            quittance: null,
            confirmation: false,
          });
        }
      } catch (error) {
        const status = error.response?.status ?? 500;
        setCode(status);
        navigate("/Error");
      } finally {
        setShowLoader(false);
      }
    };

    fetchDerogation();
  }, [id, navigate, reset, setCode]);

  const sendUpdate = async (formData, { isUpdate = false } = {}) => {
    const formDataToSend = new FormData();

    const fieldMap = {
      carteNational: "carte_national",
      extraitNaissance: "extrait_naissance",
      extraitNaissanceEpouse: "extrait_naissance_epouse",
      contratLoyer: "copie_contrat_loyer",
      quittance: "copie_derniere_quittance",
      nom: "nom",
      prenom: "prenom",
      nin: "nin",
      birthExtractNumber: "numero_certificat",
      email: "email",
      telephone: "telephone",
    };

    const errorMessagesMap = {
      fr: {
        "nom.required": "Le nom est obligatoire.",
        "prenom.required": "Le prénom est obligatoire.",
        "nin.required": "Le NIN est obligatoire.",
        "nin.size": "Le NIN doit contenir exactement 18 caractères.",
        "telephone.required": "Le numéro de téléphone est obligatoire.",
        "telephone.size": "Le numéro de téléphone doit contenir exactement 10 chiffres.",
        "email.email": "L'adresse email n'est pas valide.",
        "carte_national.mimes": "La carte nationale doit être un fichier PDF.",
        "extrait_naissance.mimes": "L'extrait de naissance doit être un fichier PDF.",
        "extrait_naissance_epouse.mimes": "L'extrait de naissance de l'épouse doit être un fichier PDF.",
        "copie_contrat_loyer.mimes": "Le contrat de loyer doit être un fichier PDF.",
        "copie_derniere_quittance.mimes": "La dernière quittance doit être un fichier PDF.",
      },
      ar: {
        "nom.required": "الاسم مطلوب.",
        "prenom.required": "اللقب مطلوب.",
        "nin.required": "رقم التعريف الوطني مطلوب.",
        "nin.size": "رقم التعريف الوطني يجب أن يتكون من 18 رقمًا.",
        "telephone.required": "رقم الهاتف مطلوب.",
        "telephone.size": "رقم الهاتف يجب أن يتكون من 10 أرقام.",
        "email.email": "البريد الإلكتروني غير صالح.",
        "carte_national.mimes": "بطاقة الهوية يجب أن تكون ملف PDF.",
        "extrait_naissance.mimes": "شهادة الميلاد يجب أن تكون ملف PDF.",
        "extrait_naissance_epouse.mimes": "شهادة ميلاد الزوجة يجب أن تكون ملف PDF.",
        "copie_contrat_loyer.mimes": "عقد الإيجار يجب أن يكون ملف PDF.",
        "copie_derniere_quittance.mimes": "آخر وصل إيجار يجب أن يكون ملف PDF.",
      },
    };

    Object.entries(fieldMap).forEach(([key, formKey]) => {
      const value = formData[key];
      if (value instanceof File) {
        formDataToSend.append(formKey, value);
      } else if (value !== null && value !== undefined && value !== "") {
        formDataToSend.append(formKey, value);
      }
    });

    if (!isUpdate) {
      formDataToSend.append("statut", "A l'Opgi");
    }

    try {
      setShowLoader(true);

      const response = await api.post(`/api/derogations/${id}`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status >= 200 && response.status < 300) {
        const message = isUpdate
          ? i18n.language === "ar"
            ? "تم حفظ التحديثات بنجاح"
            : "Modifications enregistrées avec succès"
          : i18n.language === "ar"
          ? "تم إرسال الطلب بنجاح"
          : "Demande envoyée avec succès";

        setNotification({
          type: "success",
          message,
        });

        setTimeout(() => {
          reset(defaultValues);
          navigate("/derogation");
        }, 2000);
      }
    } catch (error) {
      const status = error.response?.status ?? 500;

      if (status === 422) {
        const validationErrors = error.response.data.errors;

        const reverseFieldMap = Object.fromEntries(
          Object.entries(fieldMap).map(([reactField, laravelField]) => [
            laravelField,
            reactField,
          ])
        );

        Object.entries(validationErrors).forEach(([laravelField, messages]) => {
          const reactField = reverseFieldMap[laravelField] || laravelField;
          const firstMessage = messages[0] ?? "";

          let rule = null;
          if (/must be a file of type/i.test(firstMessage)) {
            rule = "mimes";
          } else {
            const match = firstMessage.match(/\b(required|email|size)\b/i);
            rule = match?.[1]?.toLowerCase();
          }

          const key = `${laravelField}.${rule}`;
          const translated = errorMessagesMap[i18n.language]?.[key] || firstMessage;

          setError(reactField, {
            type: "server",
            message: translated,
          });
        });

        return;
      }

      setNotification({
        type: "error",
        message:
          i18n.language === "ar"
            ? "حدث خطأ أثناء معالجة الطلب"
            : "Une erreur est survenue lors du traitement",
      });

      setCode(status);
      setTimeout(() => navigate("/Error"), 2000);
    } finally {
      setShowLoader(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const showResponse = (response) => {
    api
      .post("/api/captcha", {
        response: response.response,
      })
      .then((res) => {
        setRecaptchaIsValid(res.data.success);
      });
  };

  const handleCancel = () => {
    if (
      window.confirm(
        i18n.language === "ar"
          ? "هل أنت متأكد من أنك تريد الإلغاء؟"
          : "Êtes-vous sûr d'annuler?"
      )
    ) {
      navigate("/derogation");
    }
  };

  return (
    <div className="update-derogation-container">
      {notification && (
        <NotificationToast
          type={notification.type}
          message={notification.message}
        />
      )}

      <Loader show={showLoader} />

      {/* Breadcrumb */}
      <div className="breadcrumb-wrapper">
        <nav className="breadcrumb-nav">
          <a href="/" className="breadcrumb-link">
            {t('home')}
          </a>
          <span className="breadcrumb-separator">
            <FaChevronRight size={12} />
          </span>
          <a href="/derogation" className="breadcrumb-link">
            {t('derogation_requests')}
          </a>
          <span className="breadcrumb-separator">
            <FaChevronRight size={12} />
          </span>
          <span className="breadcrumb-current">
            {t('derogation_edit')}
          </span>
        </nav>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">
          {t('derogation_view_title')}
        </h1>
        <p className="page-subtitle">
          {t('derogation_subtitle')}
        </p>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit((data) => sendUpdate(data, { isUpdate: false }))}
        className="update-form"
        dir={getTextDirection(i18n.language)}
      >
        {/* Personal Information Section */}
        <fieldset className="form-section">
          <legend className="section-title">
            {t('personal_information')}
          </legend>

          <div className="form-grid">
            {fieldsConfig.map(({ name, label, required }) => (
              <div key={name} className="form-group">
                <label htmlFor={name} className="form-label">
                  {label[i18n.language === "ar" ? "ar" : "fr"]}
                  {required && <span className="required">*</span>}
                </label>
                <Controller
                  name={name}
                  control={control}
                  rules={{
                    ...(required && {
                      required: t('this_field_required'),
                    }),
                    ...(name === "email" && {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('invalid_email'),
                      },
                    }),
                  }}
                  render={({ field, fieldState }) => (
                    <>
                      <input
                        {...field}
                        id={name}
                        type={name === "email" ? "email" : "text"}
                        value={field.value ?? ""}
                        className={`form-input ${fieldState.invalid ? "error" : ""}`}
                        placeholder={label[i18n.language === "ar" ? "ar" : "fr"]}
                      />
                      {fieldState.invalid && (
                        <span className="error-message">
                          {errors[name]?.message}
                        </span>
                      )}
                    </>
                  )}
                />
              </div>
            ))}
          </div>
        </fieldset>

        {/* Documents Section */}
        <fieldset className="form-section">
          <legend className="section-title">
            {t('required_documents')}
          </legend>

          <div className="documents-grid">
            {[
              {
                name: "carteNational",
                labelFr: "Copie de la carte nationale",
                labelAr: "نسخة من بطاقة التعريف الوطنية",
              },
              {
                name: "extraitNaissance",
                labelFr: "Copie de l'extrait de naissance",
                labelAr: "نسخة من شهادة الميلاد",
              },
              {
                name: "extraitNaissanceEpouse",
                labelFr: "Copie de l'extrait de naissance de l'épouse",
                labelAr: "نسخة من شهادة ميلاد الزوجة",
              },
              {
                name: "contratLoyer",
                labelFr: "Copie du contrat de location",
                labelAr: "نسخة من عقد الكراء",
              },
              {
                name: "quittance",
                labelFr: "Copie de la dernière quittance de loyer",
                labelAr: "نسخة من آخر وصل إيجار",
              },
            ].map(({ name, labelFr, labelAr }) => (
              <ControlledFileUploadWithView
                key={name}
                name={name}
                control={control}
                language={i18n.language}
                derogationId={id}
                fieldName={name}
                label={i18n.language === "ar" ? labelAr : labelFr}
                rules={{
                  validate: (file) =>
                    !file || file.size <= 512 * 1024
                      ? true
                      : t('file_exceeds_512kb'),
                }}
              />
            ))}
          </div>
        </fieldset>

        {/* Captcha Section */}
        <fieldset className="form-section captcha-section">
          <legend className="section-title">
            {t('security_verification')}
          </legend>

          <div className="captcha-wrapper">
            <div className="g-recaptcha" data-sitekey={captcah_site_key}>
              <iframe
                title="Captcha"
                src={`https://www.google.com/recaptcha/api/embed?k=${captcah_site_key}`}
                width="100%"
                height="78"
                frameBorder="0"
              ></iframe>
            </div>
            {!recaptchaIsValid && (
              <p className="captcha-note">
                {t('please_verify_captcha')}
              </p>
            )}
            {recaptchaIsValid && (
              <div className="captcha-success">
                <FaCheckCircle className="captcha-icon" />
                <span>
                  {t('verified')}
                </span>
              </div>
            )}
          </div>
        </fieldset>

        {/* Confirmation Section */}
        <fieldset className="form-section confirmation-section">
          <legend className="section-title">
            {t('confirmation')}
          </legend>

          <div className="checkbox-wrapper">
            <Controller
              name="confirmation"
              control={control}
              rules={{
                required: t('confirm_accuracy'),
              }}
              render={({ field, fieldState }) => (
                <>
                  <input
                    type="checkbox"
                    id="confirmation"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className={`checkbox-input ${fieldState.invalid ? "error" : ""}`}
                  />
                  <label htmlFor="confirmation" className="checkbox-label">
                    {t('confirm_all_info_correct')}
                  </label>
                  {fieldState.invalid && (
                    <span className="error-message">{errors.confirmation?.message}</span>
                  )}
                </>
              )}
            />
          </div>
        </fieldset>

        {/* Action Buttons */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
          >
            {t('cancel')}
          </button>

          <button
            type="button"
            className="btn btn-success"
            onClick={handleSubmit((data) => sendUpdate(data, { isUpdate: true }))}
            disabled={!isValid || !recaptchaIsValid}
          >
            {t('save_updates')}
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={!isValid || !recaptchaIsValid || !confirmationValue}
          >
            <FaPaperPlane className="btn-icon" />
            {t('send_request')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateDerogation;
